import { DateTime } from "luxon";

/**
 * The two timestamp columns, available on every tab.
 *
 * Separate from screeningFields.js because these are *row* metadata, not screening_data: they come
 * off the ranking payload (the Resume's own created_at/updated_at), they exist on every tab, and
 * they're a fixed pair rather than a registry derived from whatever the webhook happened to send.
 *
 * The `meta:` prefix keeps the key space disjoint from screening_data's. Both selections flow
 * through one ColumnPicker, and screening_data keys come from a vendor payload we don't control —
 * a field named `createdAt` landing there would otherwise silently toggle this column.
 */
export const META_FIELDS = [
  { key: "meta:createdAt", label: "Created", rowKey: "createdAt" },
  { key: "meta:updatedAt", label: "Updated", rowKey: "updatedAt" },
  // "Decided" is not "Updated": Updated bumps on any write, Decided only when a recruiter acted on
  // the decision. Same registry, same picker, same formatter — it's another row timestamp that
  // reads as an em-dash until it has a value (here: nobody has decided on this candidate yet).
  { key: "meta:decidedAt", label: "Decided", rowKey: "decidedAt" },
];

/** Off by default: the default tab's grid is already 12 tracks wide. Opt in via the picker. */
export const DEFAULT_META_COLUMNS = [];

export const isMetaKey = (key) => typeof key === "string" && key.startsWith("meta:");

/**
 * An instant → the viewer's wall clock. The server sends an offset-bearing ISO string
 * (`as_utc_iso`), so luxon renders it in the browser's zone with no conversion of our own — the
 * client does the zone math, the server sends the instant. See CLAUDE.md.
 *
 * Null for every resume uploaded before the timestamp migration: there was no creation time to
 * recover, so it reads as an em-dash rather than a fabricated date.
 */
export const formatTimestamp = (iso) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return "—";
  // Year only when it isn't the current one — the column is 130px and the year is the least
  // informative part of a timestamp a recruiter is scanning for "was this yesterday or March".
  return dt.year === DateTime.now().year
    ? dt.toFormat("dd LLL, HH:mm")
    : dt.toFormat("dd LLL yyyy");
};

/** The full instant, for the cell tooltip — what the truncated display is hiding. */
export const fullTimestamp = (iso) => {
  if (!iso) return "Not recorded";
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toLocaleString(DateTime.DATETIME_FULL) : "Not recorded";
};
